import { Box, Divider, Stack, Typography } from "@mui/material";

import {
  ShareTfcViaEmail,
  TfcBannerBox,
  TfcHistorySection,
  TfcReadOnlyTable,
} from "components/TotalFreightCost";
import { EVOTRUX_BORDER_GREY } from "constants/index";
import { formatCentsWithCurrency } from "utils";

import { TfcApproveButton } from "./TfcApproveButton";
import { TfcRejectButton } from "./TfcRejectButton";

interface TfcShipperDisplayProps {
  tfcData: Extract<TfcResponse, { exists: true }>;
  shipmentId: number;
  shipperCompanyName: string;
  onTfcUpdated: () => void;
}

export function TfcShipperDisplay(props: TfcShipperDisplayProps) {
  const { tfcData, shipmentId, shipperCompanyName, onTfcUpdated } = props;
  const { tfc, available_operations: ops } = tfcData;
  const { latest_revision: revision } = tfc;

  const carrierName = `${revision.submitted_by.user.first_name} ${revision.submitted_by.user.last_name}`;
  const carrierCompany = revision.submitted_by.company.name;
  const formattedTotal = formatCentsWithCurrency(tfc.total_cents, tfc.currency);
  const shipperNote = revision.action?.notes;

  return (
    <Box>
      {tfc.state === "PENDING_REVIEW" && (
        <TfcBannerBox borderColor="#ED6C02" backgroundColor="#FFF3E0">
          <strong>{carrierName}</strong>
          {" from "}
          <strong>{carrierCompany}</strong>
          {
            " has submitted their final charges for this shipment, excluding taxes. Please review for accuracy and confirm."
          }
        </TfcBannerBox>
      )}
      {tfc.state === "APPROVED" && (
        <TfcBannerBox borderColor="#2E7D32" backgroundColor="#E8F5E9">
          {
            "You have confirmed that the total freight costs for this shipment are "
          }
          <strong>{formattedTotal}</strong>.
          {
            " You can now send a summary of the confirmed charges to your accounting team below."
          }
        </TfcBannerBox>
      )}
      {tfc.state === "NEEDS_RESUBMISSION" && (
        <TfcBannerBox borderColor="#D32F2F" backgroundColor="#FFEBEE">
          {"You've requested changes to the charges submitted by "}
          <strong>{carrierName}</strong>
          {" from "}
          <strong>{carrierCompany}</strong>.
        </TfcBannerBox>
      )}

      <TfcReadOnlyTable
        lineItems={revision.line_items}
        totalCents={revision.total_cents}
        currency={tfc.currency}
        bookedPriceCents={tfcData.booked_price_cents}
      />

      {(revision.notes ?? shipperNote) && (
        <Box
          sx={{
            backgroundColor: "#FAFAFA",
            border: `1px solid ${EVOTRUX_BORDER_GREY}`,
            borderRadius: "4px",
            p: 2,
            mb: 3,
            mt: 2,
          }}
        >
          {revision.notes && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}>
                Note from {carrierName}
                {" ("}
                {carrierCompany}
                ):
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {revision.notes}
              </Typography>
            </Box>
          )}
          {revision.notes && shipperNote && (
            <Divider sx={{ borderColor: "#EEEEEE", my: 1.5 }} />
          )}
          {shipperNote && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}>
                Note from you:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {shipperNote}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {tfc.state === "APPROVED" && (
        <ShareTfcViaEmail tfcId={tfc.tfc_id} shipmentId={shipmentId} />
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {ops.includes("APPROVE") && (
          <TfcApproveButton
            tfcId={tfc.tfc_id}
            shipmentId={shipmentId}
            carrierCompanyName={carrierCompany}
            shipperCompanyName={shipperCompanyName}
            totalFormatted={formattedTotal}
            onSuccess={onTfcUpdated}
          />
        )}
        {ops.includes("REJECT") && (
          <TfcRejectButton tfcId={tfc.tfc_id} onSuccess={onTfcUpdated} />
        )}
        {ops.includes("UNDO_AND_ACCEPT") && (
          <TfcApproveButton
            tfcId={tfc.tfc_id}
            shipmentId={shipmentId}
            carrierCompanyName={carrierCompany}
            shipperCompanyName={shipperCompanyName}
            totalFormatted={formattedTotal}
            onSuccess={onTfcUpdated}
            undoReject
          />
        )}
        {ops.includes("REOPEN") && (
          <TfcRejectButton
            tfcId={tfc.tfc_id}
            onSuccess={onTfcUpdated}
            undoConfirm
          />
        )}
      </Stack>

      <TfcHistorySection
        revisions={tfc.revisions}
        currency={tfc.currency}
        bookedPriceCents={tfcData.booked_price_cents}
      />
    </Box>
  );
}
